// client/src/services/shopService.js - Optimiert für MongoDB mit verbesserten Suchfunktionen
import api from './api';
import { calculateDistance } from './mapService';
import {getCurrentUser} from "./authService";

/**
 * Ruft alle Shops mit Filteroptionen ab
 * @param {Object} filters - Filteroptionen für die Shops
 * @returns {Promise<Object>} - Gefilterte Shop-Liste
 */
export const fetchShops = async (filters = {}) => {
    try {
        console.log('Shops werden mit folgenden Filtern abgerufen:', filters);

        // API-Anfrage mit Filterparametern
        const response = await api.get('/shops', filters);

        if (response.success) {
            console.log(`${response.data.length} Shops gefunden`);
        } else {
            console.error('Fehler beim Abrufen der Shops:', response.message);
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Abrufen der Shops:', error);
        return {
            success: false,
            message: 'Fehler beim Laden der Shops',
            data: []
        };
    }
};

/**
 * Ruft einen Shop anhand seiner ID ab
 * @param {string} id - Shop-ID
 * @returns {Promise<Object>} - Shop-Daten
 */
export const fetchShopById = async (id) => {
    try {
        console.log(`Shop mit ID ${id} wird abgerufen`);

        if (!id) {
            console.error('Shop-ID ist null oder undefiniert');
            return {
                success: false,
                message: 'Ungültige Shop-ID'
            };
        }

        // API-Anfrage für einen bestimmten Shop
        const response = await api.get(`/shops/${id}`);

        if (response.success) {
            console.log('Shop erfolgreich abgerufen');
            // Hinzugefügtes Logging für Koordinatenstruktur
            if (response.data && response.data.coordinates) {
                console.log('ShopDetailPage - Koordinatenstruktur:', response.data.coordinates);
            } else if (response.data) {
                console.log('ShopDetailPage - Shop hat keine Koordinaten.');
            }
            // Ende des hinzugefügten Loggings
        } else {
            console.error('Fehler beim Abrufen des Shops:', response.message);
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Abrufen des Shops:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Laden der Shop-Details'
        };
    }
};

/**
 * Ruft empfohlene Shops für die Startseite ab
 * @returns {Promise<Object>} - Liste der empfohlenen Shops
 */
export const fetchFeaturedShops = async () => {
    try {
        console.log('Empfohlene Shops werden abgerufen (nur Professional)');

        // Versuche verschiedene API-Endpunkte, beginnend mit dem spezifischen Featured-Endpunkt
        let response = null;
        let featuredFound = false;

        // Versuch 1: Featured-Endpunkt (mit Plan-Filter, falls Backend unterstützt)
        try {
            // IDEAL: Backend sollte Filter unterstützen: /shops/featured?plan=professional
            response = await api.get('/shops/featured', { plan: 'professional' });
            if (response.success && Array.isArray(response.data)) {
                featuredFound = true;
                console.log('Featured-Shops (Professional) über /shops/featured gefunden');
            }
        } catch (err) {
            console.warn('Featured-Endpunkt (mit Plan) nicht verfügbar:', err);
        }

        // Versuch 2: Standard-Endpunkt mit Parametern (mit Plan-Filter)
        if (!featuredFound) {
            try {
                 // IDEAL: Backend sollte Filter unterstützen
                response = await api.get('/shops', {
                    // featured: true, // Nicht mehr nötig, wenn wir nach Plan filtern
                    plan: 'professional', // Neuer Filter
                    limit: 50, // Höheres Limit für lokale Filterung nach Stadt später
                    sort: 'rating', // Oder eine andere sinnvolle Sortierung
                    order: 'desc'
                });
                if (response.success && Array.isArray(response.data)) {
                    featuredFound = true;
                    console.log('Featured-Shops (Professional) über Standard-Endpunkt mit Plan-Filter gefunden');
                }
            } catch (err) {
                console.warn('Standard-Endpunkt für Professional-Shops nicht verfügbar:', err);
            }
        }

        // Versuch 3: Abrufen aller Shops und lokal filtern (nur Professional)
        if (!featuredFound) {
            try {
                // Weniger ideal: Alle Shops laden und filtern
                response = await api.get('/shops', { limit: 200 }); // Limit erhöhen, um mehr Chancen auf Professional zu haben
                if (response.success && Array.isArray(response.data)) {
                    console.log(`Fallback: ${response.data.length} Shops geladen, filtere lokal nach Professional...`);
                    // Lokal NUR nach Professional filtern
                    const allShops = response.data;
                    const filtered = allShops
                        .filter(shop =>
                            shop.isActive &&
                            shop.subscriptionPlan === 'professional'
                         )
                        .sort((a, b) => (b.rating || 0) - (a.rating || 0))

                    response = {
                        success: true,
                        data: filtered,
                        message: 'Featured-Shops (Professional) lokal gefiltert'
                    };

                    featuredFound = true;
                    console.log('Featured-Shops (Professional) durch lokale Filterung gefunden:', filtered.length);
                } else {
                     console.warn('Fallback: Konnte keine Shops zum lokalen Filtern laden:', response.message);
                 }
            } catch (err) {
                console.warn('Abrufen aller Shops für lokale Professional-Filterung nicht möglich:', err);
            }
        }

        if (response && response.success) {
            console.log(`${response.data.length} empfohlene Professional-Shops gefunden`);
        } else {
            console.error('Fehler beim Abrufen der empfohlenen Professional-Shops:', response?.message);
            // Stelle sicher, dass ein konsistentes Fehlerobjekt zurückgegeben wird
            return {
                success: false,
                message: response?.message || 'Keine empfohlenen Professional-Shops gefunden oder Fehler aufgetreten.',
                data: []
            };
        }

        // Gib die (potenziell ungefilterten nach Stadt) Professional-Shops zurück
        return response;

    } catch (error) {
        console.error('Schwerwiegender Fehler beim Abrufen der empfohlenen Professional-Shops:', error);
        return {
            success: false,
            message: 'Fehler beim Laden der empfohlenen Professional-Shops.',
            data: []
        };
    }
};

/**
 * Sucht Shops anhand verschiedener Parameter über den Backend-Such-Endpunkt
 * @param {Object} params - Suchparameter (z.B. location, distance, lat, lng, open, brands, minRating)
 * @returns {Promise<Object>} - Suchergebnisse
 */
export const searchShops = async (params = {}) => {
    try {
        console.log('Starte Backend Shop-Suche mit Frontend-Parametern:', params);

        // 1. Backend-Parameter vorbereiten
        const backendParams = {};

        // Standort-Parameter mappen
        if (params.location && typeof params.location === 'string') {
            backendParams.location = params.location;
        }
        if (params.lat) {
            backendParams.lat = params.lat;
        }
        if (params.lng) {
            backendParams.lng = params.lng;
        }

        // Entfernungsradius mappen (Backend erwartet 'radius')
        if (params.distance) {
            backendParams.radius = params.distance;
        }

        // --- WICHTIGER HINWEIS ---
        // Die folgenden Filter werden vom Backend-Endpunkt /api/shops/search jetzt unterstützt.
        // TODO: Backend /api/shops/search erweitern, um Filter für open, brands, minRating zu unterstützen. (ERLEDIGT)
        // console.warn('searchShops: Filter für open, brands, minRating werden aktuell ignoriert, da Backend /api/shops/search sie nicht unterstützt.');
        if (params.open) { backendParams.open = 'true'; } // Backend erwartet String 'true'
        if (params.brands && params.brands.length > 0) { backendParams.brands = params.brands.join(','); } // Backend erwartet kommaseparierte Liste
        if (params.minRating && params.minRating > 0) { backendParams.minRating = params.minRating; }

        // Optional: Paginierung hinzufügen, falls das Frontend dies steuern soll
        // if (params.page) backendParams.page = params.page;

        console.log('Bereinigte Parameter für Backend-Suche:', backendParams);

        // 2. Backend-Suche aufrufen
        // Korrigierte Übergabe: Parameter direkt als zweites Argument, wie es viele HTTP-Clients (nicht nur Axios-Wrapper) erwarten.
        // Wenn api.js ein spezifisches Format braucht, muss das hier angepasst werden.
        const actualResponse = await api.get('/shops/search', backendParams);

        if (!actualResponse.success || !Array.isArray(actualResponse.data)) {
            console.error('Fehler bei der Backend-Shop-Suche oder keine Daten:', actualResponse);
            return {
                success: false,
                message: actualResponse.message || 'Fehler bei der Shop-Suche',
                data: [],
                total: 0,
                pagination: {}
            };
        }

        console.log(`Backend-Suche erfolgreich: ${actualResponse.count} Shops gefunden (von ${actualResponse.total} insgesamt)`);

        // 3. Lokale Filterung (ENTFERNT) - Das Backend liefert bereits gefilterte Daten
        // Hinzugefügtes Logging für Koordinatenstruktur (erster Shop)
        if (actualResponse.data && actualResponse.data.length > 0 && actualResponse.data[0].coordinates) {
            console.log('ShopListPage - Koordinatenstruktur (erster Shop):', actualResponse.data[0].coordinates);
        } else if (actualResponse.data && actualResponse.data.length > 0) {
            console.log('ShopListPage - Erster Shop hat keine Koordinaten.');
        }
        // Ende des hinzugefügten Loggings

        // 4. Ergebnis zurückgeben (im Format, das ShopListPage erwartet)
        return {
            success: true,
            data: actualResponse.data || [], // Stelle sicher, dass data immer ein Array ist
            total: actualResponse.total || 0,
            pagination: actualResponse.pagination || {}
            // message kann optional hinzugefügt werden, falls nützlich
        };

    } catch (error) {
        console.error('Schwerwiegender Fehler in searchShops:', error);
        // Detailliertere Fehlermeldung, falls verfügbar
        const message = error.response?.data?.message || error.message || 'Unbekannter Fehler bei der Shop-Suche';
        return {
            success: false,
            message: message,
            data: [],
            total: 0,
            pagination: {}
        };
    }
};

/**
 * Registriert einen neuen Shop
 * @param {Object} shopData - Shop-Daten (inkl. Bild-Referenzen nach separatem Upload)
 * @param {string} token - Authentifizierungstoken
 * @returns {Promise<Object>} - Ergebnis der Registrierung
 */
export const registerShop = async (shopData, token) => {
    try {
        // Bilder wurden bereits über /api/uploads hochgeladen.
        // shopData.images sollte jetzt ein Array von Objekten mit { url, publicId, ... } sein.
        console.log('Shop-Registrierung (JSON) gestartet mit Daten (inkl. Bild-Metadaten):', shopData);

        // Bereinige Bilddaten, nur relevante Felder senden
        const finalShopData = {
            ...shopData,
            images: shopData.images && Array.isArray(shopData.images)
                ? shopData.images.map(img => ({
                    url: img.url,
                    publicId: img.publicId,
                    name: img.name,
                    // path, size, type sind optional, können weggelassen werden
                }))
                : []
        };

        const response = await api.post('/shops', finalShopData, token);
        return response;

    } catch (error) {
        console.error('Fehler bei der Shop-Registrierung:', error);
        // Stelle sicher, dass die Fehlerantwort die Backend-Fehlermeldung enthält, falls verfügbar
        const errorMessage = error?.response?.data?.message || error.message || 'Fehler bei der Shop-Registrierung';
        const errors = error?.response?.data?.errors; // Optional: Detaillierte Validierungsfehler

        return {
            success: false,
            message: errorMessage,
            ...(errors && { errors: errors }) // Füge Validierungsfehler hinzu, falls vorhanden
        };
    }
};

/**
 * Aktualisiert einen bestehenden Shop
 * @param {string} id - Shop-ID
 * @param {Object} shopData - Zu aktualisierende Shop-Daten
 * @returns {Promise<Object>} - Ergebnis der Aktualisierung
 */
export const updateShop = async (id, shopData) => {
    try {
        // Bilder wurden bereits über /api/uploads hochgeladen oder über den Uploader entfernt.
        // shopData.images sollte das aktuelle Array von Bildobjekten { url, publicId, ... } enthalten.
        console.log(`Shop mit ID ${id} wird aktualisiert (JSON, inkl. Bild-Metadaten):`, shopData);

        // Bereinige Bilddaten, nur relevante Felder senden
        const finalShopData = {
            ...shopData,
            images: shopData.images && Array.isArray(shopData.images)
                ? shopData.images.map(img => ({
                    url: img.url,
                    publicId: img.publicId,
                    name: img.name,
                }))
                : []
        };

        const response = await api.put(`/shops/${id}`, finalShopData);
        return response;

    } catch (error) {
        console.error('Fehler beim Aktualisieren des Shops:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Aktualisieren des Shops'
        };
    }
};

/**
 * Löscht einen Shop
 * @param {string} id - Shop-ID
 * @returns {Promise<Object>} - Ergebnis des Löschvorgangs
 */
export const deleteShop = async (id) => {
    try {
        console.log(`Shop mit ID ${id} wird gelöscht`);

        if (!id) {
            return {
                success: false,
                message: 'Ungültige Shop-ID'
            };
        }

        // API-Anfrage zum Löschen des Shops
        const response = await api.delete(`/shops/${id}`);

        if (response.success) {
            console.log(`Shop mit ID ${id} erfolgreich gelöscht`);
        } else {
            console.error(`Fehler beim Löschen des Shops mit ID ${id}:`, response.message);
        }

        return response;
    } catch (error) {
        console.error('Fehler beim Löschen des Shops:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Löschen des Shops'
        };
    }
};

/**
 * Berechnet die Entfernung zwischen einem Shop und dem aktuellen Standort
 * @param {Object} shop - Shop-Objekt mit Koordinaten
 * @returns {Promise<number>} - Entfernung in Kilometern
 */
export const getDistanceToShop = async (shop) => {
    if (!shop || !shop.coordinates || !shop.coordinates.lat || !shop.coordinates.lng) {
        return null;
    }

    try {
        // Aktuelle Position abrufen
        const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 0
            });
        });

        const { latitude, longitude } = position.coords;

        // Entfernung berechnen
        return calculateDistance(
            latitude, longitude,
            shop.coordinates.lat, shop.coordinates.lng
        );
    } catch (error) {
        console.warn('Fehler beim Berechnen der Entfernung:', error);
        return null;
    }
};

/**
 * Ruft die Shops des angemeldeten Benutzers ab
 * @returns {Promise<Object>} - Die Shops des Benutzers
 */
export const fetchUserShops = async () => {
    try {
        console.log('Rufe Shops des aktuellen Benutzers ab');

        // 1. Methode: Spezifischer Endpunkt für Benutzer-Shops
        try {
            const response = await api.get('/users/me/shops');
            console.log('Antwort vom spezifischen Endpunkt:', response);

            if (response.success && Array.isArray(response.data)) {
                console.log(`${response.data.length} Shops gefunden vom Benutzer-Endpunkt`);
                return response;
            }
        } catch (endpointError) {
            console.warn('Fehler beim Abrufen vom spezifischen Endpunkt:', endpointError);
        }

        // 2. Methode: Alle Shops mit Benutzerfilter abrufen
        try {
            // Aktuellen Benutzer abrufen
            const user = await getCurrentUser();
            if (!user) {
                throw new Error('Kein angemeldeter Benutzer gefunden');
            }

            const userId = user.id || user.uid;
            console.log('Filtere Shops nach Benutzer-ID:', userId);

            const response = await api.get('/shops', { user: userId });
            console.log('Antwort mit Benutzerfilter:', response);

            if (response.success) {
                return response;
            }
        } catch (filterError) {
            console.warn('Fehler beim Filtern nach Benutzer:', filterError);
        }

        // 3. Methode: Alle Shops abrufen und manuell filtern
        try {
            const user = await getCurrentUser();
            if (!user) {
                throw new Error('Kein angemeldeter Benutzer gefunden');
            }

            const userId = user.id || user.uid;
            console.log('Manuelles Filtern nach Benutzer-ID:', userId);

            const response = await api.get('/shops');
            console.log('Alle Shops abgerufen, jetzt filtern');

            if (response.success && Array.isArray(response.data)) {
                const userShops = response.data.filter(shop =>
                    shop.user === userId ||
                    shop.user === userId.toString()
                );

                console.log(`${userShops.length} Shops manuell gefiltert`);

                return {
                    success: true,
                    data: userShops,
                    count: userShops.length,
                    message: 'Shops manuell gefiltert'
                };
            }
        } catch (manualError) {
            console.error('Fehler beim manuellen Filtern:', manualError);
        }

        return {
            success: false,
            message: 'Keine Shops gefunden',
            data: []
        };
    } catch (error) {
        console.error('Fehler beim Abrufen der Benutzer-Shops:', error);
        return {
            success: false,
            message: error.message || 'Fehler beim Laden Ihrer Shops',
            data: []
        };
    }
};

/**
 * Stellt sicher, dass Testshops existieren
 * @returns {Promise<void>}
 */
export const ensureTestShops = async () => {
    try {
        console.log('Überprüfung auf Test-Shops...');

        // Prüfen, ob bereits Shops existieren
        const response = await api.get('/shops', { limit: 1 });

        if (!response.success || !response.data || response.data.length === 0) {
            console.log('Keine Shops gefunden, erstelle Testshops...');

            // Testshops erstellen
            const testShops = [
                {
                    name: 'Test Shop Berlin',
                    ownerName: 'Test Owner',
                    email: 'test@example.com',
                    phone: '+49 123 456789',
                    address: 'Musterstraße 123',
                    postalCode: '10115',
                    city: 'Berlin',
                    country: 'Deutschland',
                    coordinates: {
                        lat: 52.52,
                        lng: 13.405
                    },
                    website: 'https://example.com',
                    description: 'Ein Test-Shop für Berlin mit einer guten Auswahl an Snus-Produkten.',
                    openingHours: [
                        { dayOfWeek: 'Monday', opens: '09:00', closes: '18:00' },
                        { dayOfWeek: 'Tuesday', opens: '09:00', closes: '18:00' },
                        { dayOfWeek: 'Wednesday', opens: '09:00', closes: '18:00' },
                        { dayOfWeek: 'Thursday', opens: '09:00', closes: '18:00' },
                        { dayOfWeek: 'Friday', opens: '09:00', closes: '18:00' },
                        { dayOfWeek: 'Saturday', opens: '10:00', closes: '16:00' },
                        { dayOfWeek: 'Sunday', opens: '', closes: '' },
                    ],
                    brands: ['Epok', 'Lyft', 'Skruf'],
                    subscriptionPlan: 'premium',
                    subscriptionStatus: 'active',
                    isVerified: true,
                    isActive: true,
                    // Zuweisung eines Beispiel-Benutzers (könnte im Backend angepasst werden)
                    user: '65f1d8a7a6e74f3d72c9b123'
                },
                {
                    name: 'Snus Kiosk Hamburg',
                    ownerName: 'Hamburg Owner',
                    email: 'hamburg@example.com',
                    phone: '+49 987 654321',
                    address: 'Hamburger Straße 45',
                    postalCode: '20095',
                    city: 'Hamburg',
                    country: 'Deutschland',
                    coordinates: {
                        lat: 53.551,
                        lng: 9.993
                    },
                    website: 'https://hamburg-snus.example.com',
                    description: 'Der beste Snus-Laden in Hamburg mit einer großen Auswahl an skandinavischen Marken.',
                    openingHours: [
                        { dayOfWeek: 'Monday', opens: '08:00', closes: '20:00' },
                        { dayOfWeek: 'Tuesday', opens: '08:00', closes: '20:00' },
                        { dayOfWeek: 'Wednesday', opens: '08:00', closes: '20:00' },
                        { dayOfWeek: 'Thursday', opens: '08:00', closes: '20:00' },
                        { dayOfWeek: 'Friday', opens: '08:00', closes: '22:00' },
                        { dayOfWeek: 'Saturday', opens: '09:00', closes: '22:00' },
                        { dayOfWeek: 'Sunday', opens: '10:00', closes: '18:00' },
                    ],
                    brands: ['General', 'Thunder', 'Siberia', 'Zyn'],
                    subscriptionPlan: 'basic',
                    subscriptionStatus: 'active',
                    isVerified: false,
                    isActive: true,
                    // Zuweisung eines anderen Beispiel-Benutzers
                    user: '65f1d8a7a6e74f3d72c9b456'
                },
                // Ein weiterer Test-Shop in Hamburg für bessere Testergebnisse
                {
                    name: 'Skandinavischer Snus Hamburg',
                    ownerName: 'Sven Nilsson',
                    email: 'sven@example.com',
                    phone: '+49 987 123456',
                    address: 'Reeperbahn 118',
                    postalCode: '20359',
                    city: 'Hamburg',
                    country: 'Deutschland',
                    coordinates: {
                        lat: 53.5495,
                        lng: 9.9622
                    },
                    website: 'https://snus-hamburg.example.com',
                    description: 'Echter schwedischer Snus direkt aus Skandinavien. Große Auswahl an Original-Produkten.',
                    openingHours: [
                        { dayOfWeek: 'Monday', opens: '10:00', closes: '22:00' },
                        { dayOfWeek: 'Tuesday', opens: '10:00', closes: '22:00' },
                        { dayOfWeek: 'Wednesday', opens: '10:00', closes: '22:00' },
                        { dayOfWeek: 'Thursday', opens: '10:00', closes: '22:00' },
                        { dayOfWeek: 'Friday', opens: '10:00', closes: '00:00' },
                        { dayOfWeek: 'Saturday', opens: '12:00', closes: '00:00' },
                        { dayOfWeek: 'Sunday', opens: '12:00', closes: '20:00' },
                    ],
                    brands: ['Göteborgs Rapé', 'General', 'Ettan', 'Skruf'],
                    subscriptionPlan: 'premium',
                    subscriptionStatus: 'active',
                    isVerified: true,
                    isActive: true,
                    // Zuweisung eines Beispiel-Benutzers
                    user: '65f1d8a7a6e74f3d72c9b789'
                }
            ];

            for (const shop of testShops) {
                try {
                    await api.post('/shops', shop);
                    console.log(`Testshop '${shop.name}' erstellt`);
                } catch (error) {
                    console.error(`Fehler beim Erstellen des Testshops '${shop.name}':`, error);
                }
            }

            console.log('Testshops wurden erstellt');
        } else {
            console.log('Shops existieren bereits in der Datenbank');
        }
    } catch (error) {
        console.error('Fehler beim Prüfen/Erstellen von Testshops:', error);
    }
};

// Funktion zum Abrufen aller verfügbaren Marken
export const fetchAllBrands = async () => {
    try {
        // Rufe den korrekten Backend-Endpunkt auf
        const response = await api.get('/shops/brands');
        if (response.success && Array.isArray(response.data)) {
            console.log('Marken erfolgreich geladen:', response.data);
            return response.data; // Gibt das Array von Marken-Strings zurück
        } else {
            console.error("Fehler beim Laden der Marken aus dem Backend:", response.message);
            return []; // Leeres Array im Fehlerfall
        }
    } catch (error) {
        console.error("Netzwerk- oder Serverfehler beim Laden der Marken:", error);
        return []; // Leeres Array im Fehlerfall
    }
};

// Funktion zum Hochladen von Bildern (Beispiel)
export const uploadImage = async (file) => {
    // Implementierung der uploadImage Funktion
};